import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Loader2, CheckCircle2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { WHATSAPP_NUMBER } from "@/components/WhatsAppFab";
import { toast } from "sonner";

const EMPTY = {
  title: "Mr", name: "", mobile: "", email: "", guardian_type: "S", guardian_name: "",
  dob: "", address: "", nominee_name: "", nominee_relation: "", interested_in: "products",
  referral_id: "", referral_name: "", sponsor_id: "", sponsor_name: "",
};

export default function Register() {
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const inputCls = "w-full h-11 rounded-lg border border-line px-3 text-sm focus:outline-none focus:border-brand";

  const submit = async (e) => {
    e.preventDefault();
    for (const [k, label] of [["name", "name"], ["mobile", "mobile"], ["email", "email"], ["guardian_name", "guardian name"], ["dob", "date of birth"], ["address", "address"], ["nominee_name", "nominee name"], ["nominee_relation", "nominee relation"]]) {
      if (!String(f[k]).trim()) { toast.error(`Please enter ${label}`); return; }
    }
    setSaving(true);
    try {
      await api.createRegistration(f);
      const g = { S: "Son of", D: "Daughter of", W: "Wife of" }[f.guardian_type];
      const msg = `New Direct Seller Registration:\n${f.title} ${f.name}\nMobile: ${f.mobile}\nEmail: ${f.email}\n${g}: ${f.guardian_name}\nDOB: ${f.dob}\nAddress: ${f.address}\nNominee: ${f.nominee_name} (${f.nominee_relation})\nInterested in: ${f.interested_in}` +
        (f.referral_id || f.referral_name ? `\nReferral: ${f.referral_name} (${f.referral_id})` : "") +
        (f.sponsor_id || f.sponsor_name ? `\nSponsor: ${f.sponsor_name} (${f.sponsor_id})` : "");
      setDone(true);
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, "_blank");
    } catch (err) {
      toast.error(err.message);
    } finally { setSaving(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-paper">
        <Header />
        <main className="mx-auto max-w-xl px-4 sm:px-6 py-16 text-center">
          <div className="w-20 h-20 rounded-full bg-brandter flex items-center justify-center mx-auto"><CheckCircle2 className="text-ok" size={44} /></div>
          <h1 className="font-display text-4xl text-ink mt-5">Registration Submitted!</h1>
          <p className="text-muted2 mt-2">Your Altos ID and password will be shared within 15 minutes via WhatsApp/SMS. Please keep them safe for future purchases.</p>
          <button onClick={() => navigate("/")} className="mt-6 h-12 px-7 rounded-full bg-brand text-white font-semibold">Back to store</button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper">
      <Header />
      <main className="mx-auto max-w-2xl px-4 sm:px-6 py-10">
        <div className="flex items-center gap-2 text-brand mb-1"><UserPlus size={18} /><span className="text-xs uppercase tracking-[0.2em] font-semibold">Join Altos</span></div>
        <h1 className="font-display text-4xl sm:text-5xl text-ink">Register as a Direct Seller</h1>
        <p className="text-muted2 mt-2">Fill in your details — we'll set up your Altos ID and share it within 15 minutes.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="grid grid-cols-[90px_1fr] gap-3">
            <select value={f.title} onChange={set("title")} className={inputCls} data-testid="reg-title">{["Mr", "Mrs", "Ms", "Dr"].map((t) => <option key={t}>{t}</option>)}</select>
            <input value={f.name} onChange={set("name")} placeholder="Full name" className={inputCls} data-testid="reg-name" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={f.mobile} onChange={set("mobile")} placeholder="Mobile number" className={inputCls} data-testid="reg-mobile" />
            <input value={f.email} onChange={set("email")} placeholder="Email" className={inputCls} data-testid="reg-email" />
          </div>
          <div className="grid grid-cols-[130px_1fr] gap-3">
            <select value={f.guardian_type} onChange={set("guardian_type")} className={inputCls}><option value="S">Son of</option><option value="D">Daughter of</option><option value="W">Wife of</option></select>
            <input value={f.guardian_name} onChange={set("guardian_name")} placeholder="Guardian name" className={inputCls} data-testid="reg-guardian" />
          </div>
          <label className="block"><span className="text-xs font-semibold text-muted2 uppercase tracking-wide">Date of Birth</span>
            <input type="date" value={f.dob} onChange={set("dob")} className={inputCls + " mt-1"} data-testid="reg-dob" /></label>
          <textarea value={f.address} onChange={set("address")} rows={2} placeholder="Full address" className="w-full rounded-lg border border-line px-3 py-2 text-sm focus:outline-none focus:border-brand" data-testid="reg-address" />
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={f.nominee_name} onChange={set("nominee_name")} placeholder="Nominee name" className={inputCls} data-testid="reg-nominee" />
            <input value={f.nominee_relation} onChange={set("nominee_relation")} placeholder="Nominee relation" className={inputCls} data-testid="reg-nominee-relation" />
          </div>
          <div>
            <span className="text-xs font-semibold text-muted2 uppercase tracking-wide">Interested in</span>
            <div className="flex gap-2 mt-1">
              {[["products", "Products"], ["business", "Business"]].map(([v, l]) => (
                <button type="button" key={v} onClick={() => setF((s) => ({ ...s, interested_in: v }))} className={`h-10 px-5 rounded-full text-sm font-semibold border ${f.interested_in === v ? "bg-brand text-white border-brand" : "border-line text-muted2"}`} data-testid={`reg-interest-${v}`}>{l}</button>
              ))}
            </div>
          </div>
          <details className="rounded-lg border border-line p-3">
            <summary className="text-sm font-semibold text-ink cursor-pointer">Referral / Sponsor (optional)</summary>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <input value={f.referral_id} onChange={set("referral_id")} placeholder="Referral ID" className={inputCls} />
              <input value={f.referral_name} onChange={set("referral_name")} placeholder="Referral name" className={inputCls} />
              <input value={f.sponsor_id} onChange={set("sponsor_id")} placeholder="Sponsor ID" className={inputCls} />
              <input value={f.sponsor_name} onChange={set("sponsor_name")} placeholder="Sponsor name" className={inputCls} />
            </div>
          </details>
          <p className="text-xs text-muted2">By submitting, you agree that your Altos ID & password will be shared within 15 minutes via WhatsApp/SMS.</p>
          <button disabled={saving} type="submit" className="w-full h-12 rounded-full bg-brand text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="reg-submit">
            {saving ? <Loader2 className="animate-spin" size={18} /> : "Submit registration"}
          </button>
        </form>
      </main>
      <Footer />
    </div>
  );
}
